import React, { FC, MouseEvent, useContext, useEffect, useState } from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  CircularProgress,
  Divider,
  Link,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import styled from 'styled-components';
import StarRating from '../StarRating/StarRating';
import ChartComponent from '../ChartComponent/ChartComponent';
import KununuToggleButtonGroup from '../ToggleButtonGroup/Kununu/KununuToggleButtonGroup';
import { Comment, InfoOutlined, PsychologyAlt } from '@mui/icons-material';
import Accordion from '../Accordion/Accordion';
import GrowthIndicator from '../GrowthIndicator/GrowthIndicator';
import { constants } from '../../constants/constants';
import { useTranslation } from 'react-i18next';
import { IScoreEntry } from '../../shared/interfaces/IScoreEntry';
import ScoreService from '../../services/ScoreService';
import NumberUtils from '../../shared/NumberUtils';
import { AuthContext } from '../../providers/AuthProvider';
import { useNavigate } from 'react-router-dom';

interface IScoreResponse {
  scores: IScoreEntry[];
  summary: {
    growthPercentage: number;
    reviewCount: number;
  };
}

interface Props {
  platform: string;
  monitoringId: string;
  companyUrl?: string;
  company: string;
  label: string;
  score: number;
  reviews: number;
  growth: number;
  isBenchmark: boolean;
  skipBackdated: boolean;
  skipBackdatedAbove: number;
  employeeReviews?: number;
  applicantReviews?: number;
  id?: number;
  createdAt?: string;
}

export const AccordionSummaryContentBox = styled(Box)`
  display: flex;
  align-items: center;
`;

const PlatformLogo = styled.img`
  max-height: 38px;
  max-width: 76px;
`;

const Platform: FC<Props> = ({
  platform,
  companyUrl,
  company,
  label,
  score,
  reviews,
  growth,
  isBenchmark = false,
  skipBackdated = false,
  skipBackdatedAbove = -1,
  employeeReviews,
  applicantReviews,
  id,
  monitoringId,
  createdAt,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { hasRole } = useContext(AuthContext);
  const [isExpanded, setIsExpanded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [scores, setScores] = useState<IScoreEntry[]>([]);
  const [kind, setKind] = useState<'total' | 'employee' | 'applicant'>('total');
  const [mode, setMode] = useState<FilterMode>('YEAR');
  const [isDisabled, setIsDisabled] = useState<boolean>(false);
  const [status, setStatus] = useState<string>('Daten werden geladen...');

  const handleModeChange = (event: MouseEvent<HTMLElement>, newMode: FilterMode | null) => {
    if (newMode) {
      setMode(newMode);
    }
  };

  const handleAiReview = () => {
    navigate('/monitoring/platform/' + monitoringId + '/ai');
  };

  useEffect(() => {
    const controller = new AbortController();

    const getScores = async () => {
      setLoading(true);
      const response = await ScoreService.getScores(
        platform,
        company,
        kind,
        mode,
        setLoading,
        controller.signal
      );
      setLoading(false);

      if (response) {
        const scoreResponse: IScoreResponse = response;
        setScores(scoreResponse.scores);
      }
    };

    if (id) {
      getScores();
    }

    return () => {
      controller.abort();
    };
  }, [company, id, kind, mode, platform, score]);

  useEffect(() => {
    const checkStatus = () => {
      let createdAtDate = new Date(2000, 1, 1);
      if (typeof createdAt !== 'undefined') {
        createdAtDate = new Date(createdAt);
      }

      const now = new Date();
      const sixHours = 6 * 3600 * 1000;
      const sixHoursAgo = new Date(now.getTime() - sixHours);

      if (score === -1 || (score === 0 && createdAtDate < sixHoursAgo)) {
        setStatus('Daten konnten nicht geladen werden. Bitte versuchen Sie es später erneut.');
        setIsDisabled(true);
      } else if (score >= 1) {
        setIsDisabled(false);
      } else {
        setStatus('Daten werden geladen...');
        setIsDisabled(true);
      }
    };

    checkStatus();
  }, [score, createdAt]);

  const showBackAbove = skipBackdatedAbove > -1 && reviews > skipBackdatedAbove;

  return (
    <Accordion
      expanded={isDisabled ? false : isExpanded}
      onExpandRequest={() => setIsExpanded((prevState) => !prevState)}
      summary={
        <>
          <AccordionSummaryContentBox
            sx={{
              flex: '1 1 10%',
              opacity: isDisabled ? 0.4 : 1,
            }}
          >
            {<PlatformLogo src={constants.platform_logos[platform]} />}
          </AccordionSummaryContentBox>
          <AccordionSummaryContentBox
            sx={{
              flex: '1 1 20%',
              overflow: 'hidden',
              mx: theme.spacing(2),
              whiteSpace: 'nowrap',
            }}
          >
            {companyUrl ? (
              <Tooltip
                title={label}
                placement="bottom-start"
                arrow
              >
                <Link
                  href={
                    platform === 'googleplace'
                      ? `https://www.google.com/maps/place/?q=place_id:${company}`
                      : companyUrl
                  }
                  variant="body1"
                  underline="none"
                  target="_blank"
                  sx={{
                    cursor: 'pointer',
                    color: theme.palette.mode === 'dark' ? 'white' : theme.palette.primary.main,
                    opacity: isDisabled ? 0.4 : 1,
                  }}
                >
                  {label}
                </Link>
              </Tooltip>
            ) : (
              <Tooltip
                title={label}
                placement="bottom-start"
                arrow
              >
                <Typography
                  variant="body1"
                  sx={{
                    cursor: 'pointer',
                    color: theme.palette.mode === 'dark' ? 'white' : theme.palette.primary.main,
                  }}
                >
                  {label}
                </Typography>
              </Tooltip>
            )}
          </AccordionSummaryContentBox>
          {!isDisabled ? (
            <>
              <AccordionSummaryContentBox sx={{ flex: '1 1 35%' }}>
                <StarRating value={score}></StarRating>
                <Typography
                  variant="body2"
                  sx={{
                    ml: theme.spacing(1),
                    color: theme.palette.mode === 'dark' ? 'white' : theme.palette.primary.main,
                  }}
                >
                  {NumberUtils.formatScore(score)} {t('of')} 5 {t('stars')}
                </Typography>
              </AccordionSummaryContentBox>
              <AccordionSummaryContentBox sx={{ flex: '2 1 25%' }}>
                <Comment fontSize="small" />
                <Typography
                  variant="body2"
                  sx={{ ml: theme.spacing(1) }}
                >
                  {reviews} {reviews > 1 ? `${t('reviews')}` : `${t('review')}`}
                </Typography>
              </AccordionSummaryContentBox>
              <AccordionSummaryContentBox sx={{ flex: '1 1 8%' }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    flexGrow: 1,
                    justifyContent: 'space-between',
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      flexDirection: 'row',
                      justifyContent: 'flex-end',
                    }}
                  >
                    <GrowthIndicator growth={growth} />
                    <Tooltip
                      title={
                        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                          <Typography variant="body2">{t('percentage_score_ytd')}</Typography>
                        </Box>
                      }
                      arrow
                    >
                      <Typography variant="body2">{growth}%</Typography>
                    </Tooltip>
                    {skipBackdated && (
                      <Box sx={{ display: 'flex', ml: theme.spacing(2), cursor: 'pointer' }}>
                        <Tooltip
                          title={t('backdated_none')}
                          placement="top"
                          arrow
                        >
                          <InfoOutlined
                            fontSize="small"
                            sx={{ color: theme.palette.primary.main }}
                          />
                        </Tooltip>
                      </Box>
                    )}
                    {showBackAbove && (
                      <Box sx={{ display: 'flex', ml: theme.spacing(2), cursor: 'pointer' }}>
                        <Tooltip
                          title={t('backdated_above', { val: skipBackdatedAbove })}
                          placement="top"
                          arrow
                        >
                          <InfoOutlined
                            fontSize="small"
                            sx={{ color: theme.palette.primary.main }}
                          />
                        </Tooltip>
                      </Box>
                    )}
                  </Box>
                  {hasRole('can-use-aireviews') && (
                    <Box
                      sx={{ display: 'flex', ml: theme.spacing(2), cursor: 'pointer' }}
                      onClick={() => handleAiReview()}
                    >
                      <Tooltip
                        title={t('aireview_analysis', { val: skipBackdatedAbove })}
                        placement="top"
                        arrow
                      >
                        <PsychologyAlt
                          fontSize="small"
                          sx={{ color: theme.palette.primary.main }}
                        />
                      </Tooltip>
                    </Box>
                  )}
                </Box>
              </AccordionSummaryContentBox>
            </>
          ) : (
            <>
              <AccordionSummaryContentBox sx={{ flex: '1 1 68%' }}>
                <Typography
                  variant="body2"
                  sx={{
                    color: theme.palette.mode === 'dark' ? 'white' : theme.palette.primary.main,
                  }}
                >
                  {status}
                </Typography>
                {status !== 'Daten werden geladen...' && (
                  <Tooltip
                    title={
                      <>
                        Mögliche Gründe:
                        <br />
                        - Es sind keine Bewertungen auf dem Portal vorhanden
                        <br />
                        - Falsche URL wurde hinterlegt
                        <br />- Es liegt ein Systemfehler vor, bitte kontaktieren Sie unseren
                        Support über das Helpcenter
                      </>
                    }
                    placement="top"
                    arrow
                  >
                    <InfoOutlined
                      fontSize="small"
                      sx={{ color: theme.palette.primary.main, ml: theme.spacing(1) }}
                    />
                  </Tooltip>
                )}
              </AccordionSummaryContentBox>
            </>
          )}
        </>
      }
      details={
        <>
          {isExpanded && (
            <>
              <Divider sx={{ ml: theme.spacing(5) }} />
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Box sx={{ ml: theme.spacing(5), my: theme.spacing(2) }}>
                  {platform.toLowerCase() === 'kununu' && (
                    <KununuToggleButtonGroup
                      reviewCount={reviews}
                      employeeReviewCount={employeeReviews || 0}
                      applicantReviewCount={applicantReviews || 0}
                      kind={kind}
                      setKind={setKind}
                    />
                  )}
                </Box>
                {loading && (
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <CircularProgress
                      sx={{ ml: theme.spacing(1) }}
                      size={24}
                    />
                  </Box>
                )}
                <ToggleButtonGroup
                  value={mode}
                  color="primary"
                  size="small"
                  exclusive
                  onChange={handleModeChange}
                  aria-label="text alignment"
                  sx={{
                    my: theme.spacing(2),
                    ml: 'auto',
                    '& .Mui-selected.MuiToggleButtonGroup-grouped': {
                      border: `1px solid ${theme.palette.primary.main}`,
                    },
                  }}
                >
                  <ToggleButton
                    value="YEAR"
                    aria-label="YEAR"
                  >
                    YEAR
                  </ToggleButton>
                  <ToggleButton
                    value="YTD"
                    aria-label="YTD"
                  >
                    YTD
                  </ToggleButton>
                </ToggleButtonGroup>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'center', height: '20vh' }}>
                {scores ? (
                  <ChartComponent
                    scores={scores}
                    mode={mode}
                  />
                ) : (
                  <Alert
                    severity="warning"
                    sx={{
                      minWidth: '450px',
                      maxWidth: '650px',
                      alignSelf: 'center',
                      my: theme.spacing(1),
                    }}
                  >
                    <AlertTitle sx={{ fontWeight: '700' }}>{t('no_records')}</AlertTitle>
                    {t('no_records_found')}
                  </Alert>
                )}
              </Box>
            </>
          )}
        </>
      }
      sx={{
        borderLeft: `6px solid ${
          isBenchmark ? theme.palette.secondary.main : theme.palette.primary.main
        }`,
      }}
    />
  );
};

export default Platform;
